<template>
  <div class="page-content pb-0">
    <Titlebar
      :incrustation="'fa-user-circle'"
      :backPath="'/configurations/watchmen'"
    >
      <h1 v-if="watchman && watchman.name">
        {{ watchman.name }}
      </h1>
    </Titlebar>
    <div class="scroll" v-if="canReadWatchmen">
      <div v-if="isFormLoading">
        <UiSpinner/>
      </div>
      <div v-if="isFormError">
        <div class="alert alert-danger mb-3" role="alert">
          {{ $t("global.error_message") }}
        </div>
      </div>
      <div class="row" v-if="!isFormLoading && !isFormError">
        <div class="col-4">
          <UiCard>
            <FormWrapper>
              <WatchmanForm v-model="watchman" />

              <div v-if="canUpdateWatchmen" class="pb-4">
                <Button2
                  @click.native.prevent="handeSaveWatchmanDetails()"
                  :disabled="!isFormModified || isFormSubmited || watchman.name === ''"
                  class="btn-brand"
                >
                  <span
                    v-if="isFormSubmited"
                    class="spinner-border spinner-border-sm ml-2"
                    role="status"
                    :aria-label="$t('global.loading')"
                  ></span>
                  {{ $t("global.save") }}
                </Button2>
              </div>

              <FormControl v-if="watchman.profiles.length">
                <FormLabel>{{ $t("watchmen.form.profiles_label") }}</FormLabel>
                <FormElement>
                  <ul class="m-0 list-unstyled d-flex flex-column" style="gap: 4px">
                    <li v-for="profile in watchman.profiles" :key="profile.id">
                      <router-link :to="`/configurations/profiles/${profile.id}`" class="d-inline-flex align-items-center">
                        <ProfileIcon :icon="profile.icon" class="mr-2" />
                        {{ profile.name }}
                      </router-link>
                    </li>
                  </ul>
                </FormElement>
              </FormControl>
              <FormControl v-if="watchman.procedures.length">
                <FormLabel>{{ $t("watchmen.form.monitoring_label") }}</FormLabel>
                <FormElement>
                  <ul class="m-0 pl-4">
                    <li
                      v-for="procedure in watchman.procedures"
                      :key="procedure.id"
                    >
                    <router-link
                      :to="{ name: 'RemoteMonitoringShow', params: { id: procedure.id } }"
                      class="text-truncate"
                    >
                      {{ procedure.name }}
                      </router-link>  
                    </li>
                  </ul>
                </FormElement>
              </FormControl>
              <FormControl v-if="canDeleteWatchmen">
                <FormLabel>{{ $t("watchmen.form.delete") }}</FormLabel>
                <FormElement>
                  <div
                    class="alert alert-info mb-3"
                    role="alert"
                    v-if="!!watchman.profiles.length || !!watchman.procedures.length"
                  >
                    {{ $t("watchmen.form.errors.no_delete") }}
                  </div>
                  <Button2
                    @click.native.prevent="handleDeleteWatchman()"
                    :disabled="
                      !!watchman.profiles.length || !!watchman.procedures.length
                    "
                  >
                    {{ $t("watchmen.form.delete") }}
                  </Button2>
                </FormElement>
              </FormControl>
            </FormWrapper>
          </UiCard>
        </div>

        <div class="col-8">
          <UiCard>
            <FormControl v-if="canBrowseChannels">
              <FormLabel>{{ $t("watchmen.form.channels_label") }}</FormLabel>
              <FormElement style="width: 100%">
                <div
                  v-if="!channels.length"
                  class="alert alert-info mb-3"
                  role="alert"
                >
                  {{ $t("watchmen.form.errors.no_channels") }}
                </div>
                <table
                  v-if="channels.length"
                  class="table w-100"
                  style="border-bottom: 1px solid #dee2e6"
                >
                  <thead>
                    <tr>
                      <th>{{ $t("watchmen.channels.type.label") }}</th>
                      <th>
                        {{ $t("watchmen.channels.value") }}
                      </th>
                      <th>{{ $t("watchmen.channels.validation") }}</th>
                      <th>{{ $t("watchmen.channels.profiles") }}</th>
                      <th>{{ $t("watchmen.channels.rm_procedures") }}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <ChannelRow
                      v-for="channel in channels"
                      :key="channel.id"
                      :channel="channel"
                      @onEditChannel="openModalEditChannel"
                    />
                  </tbody>
                </table>
              </FormElement>
            </FormControl>
            <Button2 @click.native.prevent="handleOpenModalAddChannel">
              {{ $t("watchmen.channels.add") }}
            </Button2>
          </UiCard>
        </div>
      </div>
    </div>

    <BasicModal ref="editChannelModal" :title="$t('watchmen.channels.editChannel')">
      <template v-slot:main>
        <ChannelEdit 
          :channelId="editChannelId" 
          @onSave="updateChannelsAfterChannelEdit"
          @onDelete="updateChannelsAfterChannelDeleted"
        />
      </template>
      <template v-slot:footer> </template>
    </BasicModal>

    <BasicModal
      ref="addChannelModal"
      :title="$t('watchmen.channels.addChannel')"
    >
      <template v-slot:main>
        <ChannelNew :watchmanId="watchman.id" @onSave="updateChannelsAfterChannelAdded" />
      </template>
      <template v-slot:footer> </template>
    </BasicModal>
  </div>
</template>

<script>
import axios from "axios";
import API from "@/services/API.js";
import Utils from "@/services/utils.js";
import Titlebar from "@/components/ui/Titlebar";
import FormWrapper from "@/components/ui/forms/FormWrapper";
import FormControl from "@/components/ui/forms/FormControl";
import FormLabel from "@/components/ui/forms/FormLabel";
import FormElement from "@/components/ui/forms/FormElement";
import Button2 from "@/components/ui/Button2";
import Btn from "@/components/ui/Button";
import UiCard from "@/components/ui/Card";
import Select from "@/components/ui/forms/Select";
import BasicModal from "@/components/ui/BasicModal";
import ChannelRow from "@/components/watchmen/channels/ChannelRow";
import ChannelEdit from "@/components/watchmen/channels/ChannelEdit";
import ChannelNew from "@/components/watchmen/channels/ChannelNew";
import WatchmanForm from "@/components/watchmen/WatchmanForm";
import { listInChannels } from "@/components/watchmen/list-in-channels";
import ProfileIcon from "@/components/profiles/ProfileIcon";
import UiSpinner from "@/components/ui/UiSpinner.vue";
import {useDvFlagAccess} from "../../services/featureFlag";

export default {
  name: "WatchmenForm",
  components: {
    Titlebar,
    FormWrapper,
    FormControl,
    FormLabel,
    FormElement,
    Button2,
    Btn,
    UiCard,
    Select,
    BasicModal,
    ChannelRow,
    ChannelEdit,
    ChannelNew,
    WatchmanForm,
    ProfileIcon,
    UiSpinner,
},
  props: {},
  data() {
    return {
      canReadWatchmen: useDvFlagAccess("beepiz-watchmen-can:read"),
      canUpdateWatchmen: useDvFlagAccess("beepiz-watchmen-can:update"),
      canDeleteWatchmen: useDvFlagAccess("beepiz-watchmen-can:delete"),
      canBrowseChannels: useDvFlagAccess("beepiz-watchmen-can:read"),
      isFormLoading: true,
      isFormError: false,
      isFormModified: false,
      isFormSubmited: false,
      watchman: {},
      channels: [],
      initialWatchmanInfo: {},
      currentChannel: null,
      editChannelId: null,
    };
  },
  computed: {},
  methods: {
    loadWatchman: function () {
      const id = this.$route.params.id;
      axios
        .get(API.route(API.endpoints.watchmen.show, { watchmanId: id }))
        .then(({data}) => {
          const { channels, ...rest } = data;
          this.watchman = { 
            ...rest,
            profiles: listInChannels(channels, "profiles"),
            procedures: listInChannels(channels, "rmProcedures"),
           };
          this.channels = channels;
          this.isFormLoading = false;
          this.preserveInitialWatchmanInfo(rest);
        })
        .catch((error) => {
          this.isFormError = true;
          console.log(error);
        });
    },
    preserveInitialWatchmanInfo: function (data) {
      this.isFormModified = false;
      this.initialWatchmanInfo = {
        name: data.name,
        description: data.description,
      };
    },
    handeSaveWatchmanDetails: function () {
      this.isFormSubmited = true;
      axios
        .put(
          API.route(API.endpoints.watchmen.edit, {
            watchmanId: this.watchman.id,
          }),
          !!this.watchman.procedures.length
            ? {
                description: this.watchman.description,
              }
            : {
                name: this.watchman.name,
                description: this.watchman.description,
              }
        )
        .then(() => {
          this.isFormSubmited = false;
          this.preserveInitialWatchmanInfo(this.watchman);
        })
        .catch((error) => {
          console.error("🐝", error);
        });
    },
    handleDeleteWatchman: function () {
      axios
        .delete(
          API.route(API.endpoints.watchmen.delete, {
            watchmanId: this.watchman.id,
          })
        )
        .then(() => {
          this.$router.push({ name: "WatchmenList" });
        })
        .catch((error) => {
          console.error("🐝", error);
        });
    },
    handleOpenModalAddChannel: function () {
      this.$refs["addChannelModal"].showModal();
    },
    updateChannelsAfterChannelAdded: function (channel) {
      this.channels = [ ...this.channels, channel ]
      this.$refs["addChannelModal"].hideModal();
    },
    openModalEditChannel: function (channelId) {
      this.editChannelId = channelId;
      this.$refs["editChannelModal"].showModal();
    },
    updateChannelsAfterChannelEdit: function (channel) {
      const channelIndex = this.channels.findIndex(
        (c) => c.id === channel.id
      );
      if (channelIndex > -1) {
        const updatedChannels = [ ...this.channels ];
        updatedChannels[channelIndex] = { ...channel };
        this.channels = [ ...updatedChannels ]
      }
      this.$refs["editChannelModal"].hideModal();
    },
    updateChannelsAfterChannelDeleted: function (channelId) {
      const channelIndex = this.channels.findIndex(
        (c) => c.id === channelId
      );
      if (channelIndex > -1) {
        const updatedChannels = [ ...this.channels ];
        updatedChannels.splice(channelIndex, 1);
        this.channels = [ ...updatedChannels ]
      }
      this.$refs["editChannelModal"].hideModal();
    },
  },
  created() {
    if (this.$route.params?.id) {
      this.loadWatchman();
    } else {
      console.error("No watchman Id provided");
    }
    window.Vfc.$refs.loading.hide();
  },
  watch: {
    watchman: {
      immediate: false,
      handler: function (newVal) {
        if (
          newVal.name !== this.initialWatchmanInfo.name ||
          newVal.description !== this.initialWatchmanInfo.description
        ) {
          this.isFormModified = true;
        }
      },
      deep: true,
    },
  },
};
</script>
